import api from '../../../core/api/client';

// ─── Normalizers ─────────────────────────────────────────────────────────────

const normalizeCheckout = (co) => {
  if (!co || typeof co !== 'object') return co;
  return {
    ...co,
    userId:             co.user_id             ?? co.userId             ?? null,
    checkoutDate:       co.checkout_date        ?? co.checkoutDate        ?? null,
    expectedReturnDate: co.expected_return_date ?? co.expectedReturnDate ?? null,
    returnDate:         co.return_date          ?? co.returnDate          ?? null,
    returnCondition:    co.return_condition     ?? co.returnCondition     ?? null,
  };
};

const normalizeHardwareItem = (h) => {
  if (!h || typeof h !== 'object') return h;
  return {
    ...h,
    userId:          h.user_id       ?? h.userId       ?? null,
    serialNumber:    h.serial_number ?? h.serialNumber ?? null,
    currentCheckout: h.current_checkout ? normalizeCheckout(h.current_checkout) : null,
    checkoutHistory: Array.isArray(h.checkout_history)
      ? h.checkout_history.map(normalizeCheckout)
      : [],
    createdAt: h.created_at ?? h.createdAt ?? null,
    updatedAt: h.updated_at ?? h.updatedAt ?? null,
  };
};

const normalizeList = (payload) => {
  const arr = Array.isArray(payload) ? payload : (payload?.data ?? []);
  return arr.map(normalizeHardwareItem);
};

// ─── Serializer ───────────────────────────────────────────────────────────────
// POST /hardware: { name, serial_number, category, description, metadata }
// PATCH /hardware/{id}: { name, serial_number, category, description, status, metadata }
const serializeHardwareItem = (data) => {
  if (!data || typeof data !== 'object') return {};
  const {
    serialNumber,
    // strip client-only / computed fields
    userId, createdAt, updatedAt, currentCheckout, checkoutHistory,
    ...rest
  } = data;
  const payload = { ...rest };
  if (serialNumber !== undefined) payload.serial_number = serialNumber;
  return payload;
};

// ─── Service ─────────────────────────────────────────────────────────────────

export const hardwareService = {
  // GET /hardware
  async listHardware() {
    const response = await api.get('/hardware');
    return normalizeList(response.data);
  },

  // POST /hardware  { name, serial_number, category, description, metadata }
  async createHardwareItem(data) {
    const response = await api.post('/hardware', serializeHardwareItem(data));
    return normalizeHardwareItem(response.data);
  },

  // GET /hardware/available
  async getAvailableHardware() {
    const response = await api.get('/hardware/available');
    return normalizeList(response.data);
  },

  // GET /hardware/me
  async getMyHardware() {
    const response = await api.get('/hardware/me');
    return normalizeList(response.data);
  },

  // GET /hardware/{id}
  async getHardwareItem(hardwareId) {
    const response = await api.get(`/hardware/${hardwareId}`);
    return normalizeHardwareItem(response.data);
  },

  // PATCH /hardware/{id}  { name, serial_number, category, description, status, metadata }
  async updateHardwareItem(hardwareId, data) {
    const response = await api.patch(`/hardware/${hardwareId}`, serializeHardwareItem(data));
    return normalizeHardwareItem(response.data);
  },

  // DELETE /hardware/{id}
  async deleteHardwareItem(hardwareId) {
    const response = await api.delete(`/hardware/${hardwareId}`);
    return response.data;
  },

  // POST /hardware/{id}/checkout  { expected_return_date, purpose }
  async checkoutHardware(hardwareId, { expectedReturnDate, purpose }) {
    const response = await api.post(`/hardware/${hardwareId}/checkout`, {
      expected_return_date: expectedReturnDate,
      purpose,
    });
    return normalizeHardwareItem(response.data);
  },

  // POST /hardware/{id}/checkin  { return_condition }
  async checkinHardware(hardwareId, { returnCondition = 'good' } = {}) {
    const response = await api.post(`/hardware/${hardwareId}/checkin`, {
      return_condition: returnCondition,
    });
    return normalizeHardwareItem(response.data);
  },
};
